import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { api } from "@/lib/api";
import { Card, SectionTitle } from "@/components/shared/primitives";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { shortDate } from "@/lib/format";

export default function Privacy() {
  const { data: settings, refetch } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings") });
  const { data: audit } = useQuery({ queryKey: ["audit"], queryFn: () => api.get("/settings/audit") });
  const [form, setForm] = useState(null);

  useEffect(() => { if (settings?.data) setForm(settings.data); }, [settings]);
  if (!form) return null;

  const save = async () => {
    try {
      await api.patch("/settings", {
        team_leads_see_individuals: form.team_leads_see_individuals,
        members_see_own_data: form.members_see_own_data,
        retention_raw_days: Number(form.retention_raw_days),
        retention_rollup_months: Number(form.retention_rollup_months),
        employee_notice: form.employee_notice,
        thresholds: form.thresholds,
      });
      toast.success("Privacy settings saved.");
      refetch();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const setT = (k) => (e) => setForm({ ...form, thresholds: { ...form.thresholds, [k]: Number(e.target.value) } });

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <SectionTitle className="mb-4">Privacy and access toggles</SectionTitle>
        <div className="space-y-4">
          <label className="flex items-center justify-between gap-4 py-2">
            <span className="text-[14px] text-nb-ink">Team Leads can see individual members<span className="block text-[12.5px] text-nb-ink2">Default off. Aggregates only when this is off.</span></span>
            <Switch data-testid="toggle-leads-individuals" checked={form.team_leads_see_individuals} onCheckedChange={(v) => setForm({ ...form, team_leads_see_individuals: v })} />
          </label>
          <label className="flex items-center justify-between gap-4 py-2 border-t border-nb-border">
            <span className="text-[14px] text-nb-ink">Members can see their own data<span className="block text-[12.5px] text-nb-ink2">Default on.</span></span>
            <Switch data-testid="toggle-members-own" checked={form.members_see_own_data} onCheckedChange={(v) => setForm({ ...form, members_see_own_data: v })} />
          </label>
        </div>
      </Card>

      <Card className="p-5">
        <SectionTitle className="mb-4">Retention</SectionTitle>
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div><label className="text-[13px] text-nb-ink2 block mb-1">Raw events (days)</label><Input data-testid="retention-raw" type="number" value={form.retention_raw_days} onChange={(e) => setForm({ ...form, retention_raw_days: e.target.value })} /></div>
          <div><label className="text-[13px] text-nb-ink2 block mb-1">Rollups (months)</label><Input data-testid="retention-rollup" type="number" value={form.retention_rollup_months} onChange={(e) => setForm({ ...form, retention_rollup_months: e.target.value })} /></div>
        </div>
        <p className="text-[12.5px] text-nb-ink2 mt-3">After this, data aggregates to team level only. Leavers' personal data is removed within 30 days.</p>
      </Card>

      <Card className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <SectionTitle>Status thresholds</SectionTitle>
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="status-info" aria-label="How status is calculated"
                className="w-6 h-6 inline-flex items-center justify-center rounded-md text-nb-ink2 hover:bg-[var(--bg-selected)] transition-colors">
                <Info size={16} strokeWidth={1.75} />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[320px] bg-nb-surface p-4 text-[13px] text-nb-ink" data-testid="status-info-panel">
              <div className="font-semibold mb-2">How a tool's usage status is set</div>
              <p className="text-nb-ink2 mb-2">Status uses <strong className="text-nb-ink">utilisation</strong> = active users holding a seat ÷ seats bought, over the selected period.</p>
              <ul className="space-y-1.5">
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: "var(--brand-navy)" }} /><strong className="text-nb-ink">High usage</strong>: utilisation at or above the high threshold.</li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: "var(--brand-kawaii)" }} /><strong className="text-nb-ink">Moderate usage</strong>: between the two thresholds.</li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: "var(--brand-floral)" }} /><strong className="text-nb-ink">Low usage</strong>: below the low threshold.</li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: "var(--text-muted)" }} /><strong className="text-nb-ink">Too early to tell</strong>: added within the too-early window.</li>
              </ul>
            </PopoverContent>
          </Popover>
        </div>
        <p className="text-[12.5px] text-nb-ink2 mb-4">Utilisation is a fraction between 0 and 1, so 0.75 means 75%.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div><label className="text-[12.5px] text-nb-ink2 block mb-1">High usage: utilisation ≥</label><Input data-testid="threshold-high" type="number" step="0.05" value={form.thresholds.well_used_utilisation} onChange={setT("well_used_utilisation")} /></div>
          <div><label className="text-[12.5px] text-nb-ink2 block mb-1">Low usage: utilisation &lt;</label><Input data-testid="threshold-low" type="number" step="0.05" value={form.thresholds.under_used_utilisation} onChange={setT("under_used_utilisation")} /></div>
          <div><label className="text-[12.5px] text-nb-ink2 block mb-1">Too-early window (days)</label><Input type="number" value={form.thresholds.too_early_days} onChange={setT("too_early_days")} /></div>
          <div><label className="text-[12.5px] text-nb-ink2 block mb-1">Idle seat days</label><Input type="number" value={form.thresholds.idle_seat_days} onChange={setT("idle_seat_days")} /></div>
          <div><label className="text-[12.5px] text-nb-ink2 block mb-1">Renewal alert days</label><Input type="number" value={form.thresholds.renewal_alert_days} onChange={setT("renewal_alert_days")} /></div>
        </div>
      </Card>

      <Card className="p-5">
        <SectionTitle className="mb-1">Employee notice</SectionTitle>
        <p className="text-[12.5px] text-nb-ink2 mb-3">Shown to members on their transparency page. This data is for tool and training decisions only.</p>
        <Textarea data-testid="employee-notice" rows={4} value={form.employee_notice} onChange={(e) => setForm({ ...form, employee_notice: e.target.value })} />
      </Card>

      <Button data-testid="save-privacy" onClick={save} className="rounded-full bg-nb-sage hover:bg-nb-sage/90 text-white">Save privacy settings</Button>

      <Card className="p-5" data-testid="audit-log">
        <SectionTitle className="mb-1">Audit log</SectionTitle>
        <p className="text-[12.5px] text-nb-ink2 mb-3">Every view or export of member-level data is recorded here.</p>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-[13.5px]">
            <thead><tr className="text-[12.5px] text-nb-ink2 text-left border-b border-nb-border sticky top-0 bg-nb-surface"><th className="py-2 font-medium">Who</th><th className="py-2 font-medium">Action</th><th className="py-2 font-medium">Whose data</th><th className="py-2 font-medium">When</th></tr></thead>
            <tbody>
              {(audit?.data || []).map((a) => (
                <tr key={a.id} className="border-b border-nb-border last:border-0">
                  <td className="py-2 text-nb-ink">{a.actor_name}</td>
                  <td className="py-2 text-nb-ink2">{a.action.replace(/_/g, " ")}</td>
                  <td className="py-2 text-nb-ink2">{a.subject_name || "-"}</td>
                  <td className="py-2 text-nb-ink2">{shortDate(a.created_at)}</td>
                </tr>
              ))}
              {(audit?.data || []).length === 0 && <tr><td colSpan={4} className="py-3 text-nb-ink2">No member-level views yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
